import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

export interface ErrorLogEntry {
  timestamp: string;
  message: string;
  stack?: string;
  url?: string;
  statusCode?: number;
  responseBody?: string;
  context?: string;
  userAgent?: string;
  userId?: string;
}

@Injectable({ providedIn: 'root' })
export class ErrorLoggerService {
  private readonly MAX_LOGS = 100;
  private errorLogs: ErrorLogEntry[] = [];

  constructor() {
    // Load existing logs from localStorage
    this.loadLogsFromStorage();
  }

  /**
   * Log an error with full context
   */
  logError(error: any, context?: string, userId?: string): void {
    const entry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      message: this.extractErrorMessage(error),
      context: context || 'Unknown',
      userAgent: navigator.userAgent,
      userId
    };

    // Handle HTTP errors specifically
    if (error instanceof HttpErrorResponse) {
      entry.url = error.url || 'Unknown URL';
      entry.statusCode = error.status;

      // Try to parse response body
      try {
        if (typeof error.error === 'string') {
          // Check if it's HTML (common issue with Vertex AI)
          if (error.error.trim().startsWith('<!DOCTYPE') || error.error.trim().startsWith('<html')) {
            entry.responseBody = '[HTML Error Page]';
            entry.message = `${entry.message} - Server returned HTML instead of JSON`;
          } else {
            entry.responseBody = error.error.substring(0, 500); // Limit size
          }
        } else if (error.error) {
          entry.responseBody = JSON.stringify(error.error).substring(0, 500);
        }
      } catch (e) {
        entry.responseBody = 'Could not parse response body';
      }
    }

    // Add stack trace if available
    if (error?.stack) {
      entry.stack = error.stack;
    }

    // Add to logs
    this.errorLogs.unshift(entry);

    // Keep only the last MAX_LOGS entries
    if (this.errorLogs.length > this.MAX_LOGS) {
      this.errorLogs = this.errorLogs.slice(0, this.MAX_LOGS);
    }

    // Save to localStorage
    this.saveLogsToStorage();

    // Log to console in development
    if (!this.isProduction()) {
      console.group(`🔴 Error Logged: ${entry.context}`);
      console.error('Message:', entry.message);
      console.error('Timestamp:', entry.timestamp);
      if (entry.url) console.error('URL:', entry.url);
      if (entry.statusCode) console.error('Status:', entry.statusCode);
      if (entry.responseBody) console.error('Response:', entry.responseBody);
      if (entry.stack) console.error('Stack:', entry.stack);
      console.groupEnd();
    }
  }

  /**
   * Log a network/API error with detailed information
   */
  logApiError(
    error: HttpErrorResponse,
    apiName: string,
    requestPayload?: any,
    userId?: string
  ): void {
    const context = `API: ${apiName}`;

    // Enhanced logging for API errors
    console.group(`🌐 API Error: ${apiName}`);
    console.error('URL:', error.url);
    console.error('Status:', error.status, error.statusText);
    console.error('Error:', error.error);
    if (requestPayload) {
      console.error('Request Payload:', requestPayload);
    }
    console.groupEnd();

    this.logError(error, context, userId);
  }

  /**
   * Get all error logs
   */
  getLogs(): ErrorLogEntry[] {
    return [...this.errorLogs];
  }

  /**
   * Get recent errors (last N)
   */
  getRecentLogs(count: number = 10): ErrorLogEntry[] {
    return this.errorLogs.slice(0, count);
  }

  /**
   * Search logs by context
   */
  getLogsByContext(context: string): ErrorLogEntry[] {
    return this.errorLogs.filter(log =>
      log.context?.toLowerCase().includes(context.toLowerCase())
    );
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.errorLogs = [];
    this.saveLogsToStorage();
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.errorLogs, null, 2);
  }

  /**
   * Download logs as a file
   */
  downloadLogs(filename: string = 'error-logs.json'): void {
    const dataStr = this.exportLogs();
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  /**
   * Get error statistics
   */
  getErrorStats(): {
    total: number;
    byContext: Record<string, number>;
    byStatusCode: Record<number, number>;
    last24Hours: number;
  } {
    const stats = {
      total: this.errorLogs.length,
      byContext: {} as Record<string, number>,
      byStatusCode: {} as Record<number, number>,
      last24Hours: 0
    };

    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;

    this.errorLogs.forEach(log => {
      // Count by context
      if (log.context) {
        stats.byContext[log.context] = (stats.byContext[log.context] || 0) + 1;
      }

      // Count by status code
      if (log.statusCode) {
        stats.byStatusCode[log.statusCode] = (stats.byStatusCode[log.statusCode] || 0) + 1;
      }

      // Count last 24 hours
      if (new Date(log.timestamp).getTime() > oneDayAgo) {
        stats.last24Hours++;
      }
    });

    return stats;
  }

  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      return `HTTP ${error.status}: ${error.statusText || error.message}`;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (error?.message) {
      return error.message;
    }

    return 'Unknown error occurred';
  }

  private loadLogsFromStorage(): void {
    try {
      const stored = localStorage.getItem('clarichat_error_logs');
      if (stored) {
        this.errorLogs = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Failed to load error logs from storage:', e);
    }
  }

  private saveLogsToStorage(): void {
    try {
      localStorage.setItem('clarichat_error_logs', JSON.stringify(this.errorLogs));
    } catch (e) {
      console.warn('Failed to save error logs to storage:', e);
    }
  }

  private isProduction(): boolean {
    return window.location.hostname !== 'localhost' &&
           !window.location.hostname.includes('127.0.0.1');
  }
}
